/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { useEffect } from 'react';
import {
  LDContext,
  useFlags,
  useLDClient
} from 'launchdarkly-react-client-sdk';
import { isEqual } from 'lodash';

// Refer to context definition in the backend for any changes
// https://www.notion.so/kognitos/Using-Feature-Flags-1f55700346634a7f9de4c79ad1e1a2f6?pvs=4#9d44510c6e494744bb22ad33dbdd6818
// We allow user, department, and run context for now
interface LDCIdentification {
  run?: { key: string; brain_version: string; worker_id: string };
  department?: { key: string; dept_id: string; user_id?: string };
  user?: { key: string; user_id: string };
}

interface LDCIdentificationLocal extends LDCIdentification {
  kind: string;
}

interface UseFeatureFlagParams {
  context?: LDCIdentification;
}

interface FlagValues {
  [key: string]: boolean;
}

// Check if context is ready and valid
const isContextValid = (context: LDCIdentification | undefined): boolean => {
  if (!context) return false;
  if (context.user && (!context.user.key || !context.user.user_id))
    return false;
  if (
    context.department &&
    (!context.department.key || !context.department.dept_id)
  )
    return false;
  if (
    context.run &&
    (!context.run.key || !context.run.brain_version || !context.run.worker_id)
  )
    return false;

  return true;
};

/**
 * Custom hook for getting feature flags using LaunchDarkly.
 * @param {UseFeatureFlagParams} params - The parameters for the hook.
 * @param {LDCIdentification} params.context - The custom context for identifying the user, department or run.
 * If no context is provided, the hook will use the last context that was set.
 * @returns {Array<FlagValues>} - An array containing the flag values.
 *
 * @example
 * ```typescript
 * const [{ flag1, flag2 }] = useFeatureFlag({
 *   context: {
 *     department: { key: 'dept1', dept_id: 'dept1' },
 *   }
 * });
 */
function useFeatureFlag(params?: UseFeatureFlagParams): [FlagValues] {
  // if no context is provided, the hook will use the latest context that was set
  const { context } = params || {};

  const flags = useFlags();
  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) {
      console.warn('LaunchDarkly client not initialized');
      return;
    }

    // Check if context is ready
    if (!isContextValid(context)) {
      console.warn('Context is not ready or empty');
      return;
    }

    const currentContext: (LDCIdentification & LDContext) | undefined =
      ldClient.getContext();

    let needsIdentification = !currentContext; // needs identification if there is no current context
    const identification: LDCIdentificationLocal = { kind: 'multi' };

    // we allow only context from LDCIdentification interface
    if (context?.user && !isEqual(context.user, currentContext?.user)) {
      identification.user = context.user;
      needsIdentification = true;
    }

    if (
      context?.department &&
      !isEqual(context.department, currentContext?.department)
    ) {
      identification.department = context.department;
      needsIdentification = true;
    }

    if (context?.run && !isEqual(context.run, currentContext?.run)) {
      identification.run = context.run;
      needsIdentification = true;
    }

    if (needsIdentification) {
      ldClient.identify(identification);
    }
  }, [context, ldClient]);

  return [flags];
}

export default useFeatureFlag;
